import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ImageUploader } from "@/components/ImageUploader";
import { useAuth } from "@/contexts/AuthContext";
import { Pill, MapPin, Phone, Loader2, CheckCircle2, Info } from "lucide-react";

/**
 * Pharmacy-specific customer flow.
 * Instead of a catalog, the customer uploads a photo of their prescription
 * (plus optional note + delivery address) and the pharmacy approves it manually.
 * The approved order auto-dispatches to an available rider.
 */
export const PrescriptionUploader = ({ provider, lang = "en" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [photoUrl, setPhotoUrl] = useState("");
  const [note, setNote] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);

  const isAr = lang === "ar";
  const accent = provider.accent || "#22C55E";

  const T = {
    title: isAr ? "ارفع وصفتك الطبية" : "Upload your prescription",
    subtitle: isAr
      ? "التقط صورة واضحة للوصفة — سنقوم بمراجعتها وإخبارك بالسعر ثم يقوم السائق بتوصيلها."
      : "Snap a clear photo of your prescription. The pharmacy will review it, share the price, then a rider delivers it to you.",
    photoLabel: isAr ? "صورة الوصفة" : "Prescription photo",
    photoHint: isAr ? "التقط صورة أو اختر من المعرض" : "Take a photo or pick from gallery",
    noteLabel: isAr ? "ملاحظات (اختياري)" : "Notes (optional)",
    notePlaceholder: isAr
      ? "مثال: هذا الدواء لوالدتي، عمرها 65 سنة. أحتاجه اليوم إن أمكن."
      : "e.g. This is for my mother, age 65. Need it today if possible.",
    addressLabel: isAr ? "عنوان التوصيل" : "Delivery address",
    addressPlaceholder: isAr ? "المنزل، الحي، أقرب معلم" : "House, area, nearest landmark",
    phoneLabel: isAr ? "رقم التواصل" : "Contact phone",
    submit: isAr ? "أرسل الوصفة" : "Send prescription",
    successTitle: isAr ? "تم إرسال الوصفة!" : "Prescription sent!",
    successBody: isAr
      ? "ستقوم الصيدلية بمراجعة وصفتك وإخبارك بالسعر خلال دقائق. سيتم إشعارك عبر واتساب."
      : "The pharmacy will review your prescription and share the price within minutes. You'll be notified via WhatsApp.",
    viewOrders: isAr ? "متابعة طلباتي" : "Track my orders",
    goHome: isAr ? "الرجوع للرئيسية" : "Back to home",
    step1: isAr ? "١" : "1",
    step2: isAr ? "٢" : "2",
    step3: isAr ? "٣" : "3",
    step1Label: isAr ? "ارفع الصورة" : "Upload photo",
    step2Label: isAr ? "الصيدلية تراجع" : "Pharmacy reviews",
    step3Label: isAr ? "السائق يوصل" : "Rider delivers",
    photoRequired: isAr ? "صورة الوصفة مطلوبة" : "Prescription photo is required",
    addressRequired: isAr ? "عنوان التوصيل مطلوب" : "Delivery address is required",
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photoUrl) return setErr(T.photoRequired);
    if (!address.trim()) return setErr(T.addressRequired);
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/prescriptions", {
        provider_id: provider.id,
        photo_url: photoUrl,
        note: note || null,
        delivery_address: address,
        contact_phone: phone || null,
      });
      setSuccess(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to submit prescription");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12 text-center" data-testid="prescription-success">
        <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: `${accent}1A`, color: accent }}>
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h1 className="text-3xl font-extrabold">{T.successTitle}</h1>
        <p className="text-[var(--so-text-soft)] mt-2 max-w-md mx-auto">{T.successBody}</p>
        <div className="so-card p-5 mt-6 text-start">
          <div className="text-sm text-[var(--so-text-soft)]">{isAr ? "رقم الطلب" : "Order ID"}</div>
          <div className="font-mono text-xs mt-1 break-all">{success.id}</div>
          <div className="mt-3 text-sm">
            <span className="text-[var(--so-text-soft)]">{isAr ? "الصيدلية" : "Pharmacy"}: </span>
            <span className="font-semibold">{success.pharmacy_name}</span>
          </div>
        </div>
        <div className="flex gap-3 mt-8">
          <button onClick={() => navigate("/app/orders")} className="flex-1 so-btn-secondary" data-testid="rx-view-orders">
            {T.viewOrders}
          </button>
          <button onClick={() => navigate("/app")} className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-xl text-white font-semibold" style={{ background: accent }} data-testid="rx-go-home">
            {T.goHome}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="prescription-uploader">
      <div className="so-card p-6 md:p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}1A`, color: accent }}>
            <Pill className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{provider.name}</div>
            <h2 className="font-bold text-xl leading-tight">{T.title}</h2>
          </div>
        </div>
        <p className="text-sm text-[var(--so-text-soft)] mt-1 leading-relaxed">{T.subtitle}</p>

        {/* How it works — 3 steps */}
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          {[
            { n: T.step1, label: T.step1Label },
            { n: T.step2, label: T.step2Label },
            { n: T.step3, label: T.step3Label },
          ].map((s, i) => (
            <div key={i} className="p-3 rounded-lg" style={{ background: `${accent}0A`, border: `1px solid ${accent}22` }}>
              <div className="w-7 h-7 mx-auto rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: accent }}>{s.n}</div>
              <div className="text-[11px] mt-1.5 font-semibold leading-tight">{s.label}</div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {/* Photo */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] block mb-2">
              {T.photoLabel} <span className="text-red-500">*</span>
            </label>
            <ImageUploader
              value={photoUrl}
              onChange={(url) => setPhotoUrl(url || "")}
              label={T.photoHint}
              testId="rx-photo-uploader"
              variant="square"
              accent={accent}
              lang={lang}
              maxWidth="280px"
            />
          </div>

          {/* Note */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] block mb-1">
              {T.noteLabel}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={T.notePlaceholder}
              rows={3}
              className="so-input w-full resize-none"
              data-testid="rx-note-input"
            />
          </div>

          {/* Address */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] block mb-1">
              <MapPin className="w-3.5 h-3.5 inline me-1" />{T.addressLabel} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={T.addressPlaceholder}
              className="so-input w-full"
              required
              data-testid="rx-address-input"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] block mb-1">
              <Phone className="w-3.5 h-3.5 inline me-1" />{T.phoneLabel}
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+249xxxxxxxxx"
              className="so-input w-full"
              data-testid="rx-phone-input"
              dir="ltr"
            />
          </div>

          {/* Info banner */}
          <div className="flex items-start gap-2 p-3 rounded-lg" style={{ background: `${accent}0A`, border: `1px solid ${accent}22` }}>
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accent }} />
            <div className="text-xs text-[var(--so-text-soft)] leading-relaxed">
              {isAr
                ? "الصيدلية ستراجع الوصفة يدويًا، ونتحقق تلقائيًا (بالذكاء الاصطناعي) أن الصورة وصفة طبية فعلاً. سنرسل السعر عبر واتساب قبل الشحن. الدفع عند التوصيل."
                : "We automatically verify (with AI) that your photo is a real prescription, then the pharmacy reviews it manually. We'll send the final price via WhatsApp before dispatch. Cash on delivery."}
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="rx-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-bold text-white transition-all disabled:opacity-60"
            style={{ background: accent }}
            data-testid="rx-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pill className="w-4 h-4" />}
            {T.submit}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PrescriptionUploader;
